import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	FormControlLabel,
	Popover,
	TextField,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import type {
	Tag,
	TagFilterContext,
	TagFilterType,
	TagValue,
} from "../../models/Tag";
import { getLogTagValues, getLogTags } from "../../repositories/TagsRepository";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const useSearchLogTags = ({
	startDate,
	endDate,
	logTypeCode,
	tagFilters,
}: {
	startDate: Date;
	endDate: Date | null;
	logTypeCode: string | null;
	tagFilters: TagFilterType[];
}) => {
	return useQuery<Tag[], Error>({
		queryKey: [
			"logTags",
			startDate.getTime(),
			endDate?.getTime(),
			logTypeCode,
			JSON.stringify(tagFilters),
		],
		queryFn: ({ signal }) =>
			getLogTags({ startDate, endDate, logTypeCode, tagFilters }, signal),
		enabled: !!startDate,
		staleTime: 10_000,
	});
};

const useSearchLogTagValues = ({
	tagName,
	startDate,
	endDate,
	logTypeCode,
	tagFilters,
}: {
	tagName: string;
	startDate: Date;
	endDate: Date | null;
	logTypeCode: string | null;
	tagFilters: TagFilterType[];
}) => {
	return useQuery<TagValue[], Error>({
		queryKey: [
			"logTagValues",
			tagName,
			startDate.getTime(),
			endDate?.getTime(),
			logTypeCode,
			JSON.stringify(tagFilters),
		],
		queryFn: ({ signal }) =>
			getLogTagValues(
				{ tagName, startDate, endDate, logTypeCode, tagFilters },
				signal,
			),
		enabled: !!tagName,
		staleTime: 10_000,
	});
};

type Props = {
	tagFilters: TagFilterType[];
	setTagFilters: (filters: TagFilterType[]) => void;
	filterContext: TagFilterContext;
};

export const TagFilter = ({
	tagFilters,
	setTagFilters,
	filterContext,
}: Props) => {
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
	const [selectedValues, setSelectedValues] = useState<TagValue[]>([]);
	const [showOnlyWithData, setShowOnlyWithData] = useState(true);
	const [error, setError] = useState("");

	const {
		data: availableTags,
		isLoading: isLoadingTags,
		error: tagsError,
		isError: isTagsError,
	} = useSearchLogTags({
		startDate: filterContext.startDate,
		endDate: filterContext.endDate,
		logTypeCode: filterContext.logTypeCode,
		tagFilters: filterContext.tagFilters,
	});

	const {
		data: tagValues,
		isLoading: isLoadingValues,
		error: valuesError,
		isError: isValuesError,
	} = useSearchLogTagValues({
		tagName: selectedTag ? selectedTag.TagName : "",
		startDate: filterContext.startDate,
		endDate: filterContext.endDate,
		logTypeCode: filterContext.logTypeCode,
		tagFilters: filterContext.tagFilters,
	});

	const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
		setSelectedTag(null);
		setSelectedValues([]);
	};

	const handleClosePopover = () => {
		setAnchorEl(null);
		setSelectedTag(null);
		setSelectedValues([]);
	};

	const handleAddFilter = () => {
		if (selectedTag && selectedValues.length > 0) {
			// Check if this tag already has a filter
			const existingIndex = tagFilters.findIndex(
				(f) => f.TagName === selectedTag.TagName,
			);

			const valueStrings = selectedValues.map((v) => v.Value);

			if (existingIndex >= 0) {
				// Update existing filter
				const newFilters = [...tagFilters];
				newFilters[existingIndex] = {
					TagName: selectedTag.TagName,
					Values: valueStrings,
				};
				setTagFilters(newFilters);
			} else {
				// Add new filter
				setTagFilters([
					...tagFilters,
					{
						TagName: selectedTag.TagName,
						Values: valueStrings,
					},
				]);
			}
			handleClosePopover();
		}
	};

	const handleRemoveFilter = (tagName: string) => {
		setTagFilters(tagFilters.filter((f) => f.TagName !== tagName));
	};

	const handleEditFilter = (
		event: React.MouseEvent<HTMLDivElement>,
		filter: TagFilterType,
	) => {
		const tag = availableTags?.find((t) => t.TagName === filter.TagName);
		if (tag) {
			setSelectedTag(tag);
			setSelectedValues(
				filter.Values.map((v) => ({ Value: v, HasData: true })),
			);
			setAnchorEl(event.currentTarget as unknown as HTMLButtonElement);
		}
	};

	const availableTagsForSelection =
		availableTags?.filter(
			(tag) =>
				(!tagFilters.some((f) => f.TagName === tag.TagName) ||
					selectedTag?.TagName === tag.TagName) &&
				(!showOnlyWithData || tag.HasData),
		) || [];

	const availableTagValues =
		tagValues?.filter((tv) => !showOnlyWithData || tv.HasData) || [];

	const open = Boolean(anchorEl);

	useEffect(() => {
		if (tagValues && selectedValues.length > 0) {
			const syncedValues = selectedValues
				.map((sv) => tagValues.find((tv) => tv.Value === sv.Value))
				.filter((tv): tv is TagValue => tv !== undefined);
			if (syncedValues.length > 0) {
				setSelectedValues(syncedValues);
			}
		}
	}, [tagValues]);

	useEffect(() => {
		if (isTagsError && tagsError) {
			setError(tagsError.message);
		}
		if (isValuesError && valuesError) {
			setError(valuesError.message);
		}
	}, [isTagsError, tagsError, isValuesError, valuesError]);

	return (
		<>
			<Box
				sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
			>
				<Typography variant="body2" sx={{ fontWeight: 500, color: "#666" }}>
					Tags:
				</Typography>

				{tagFilters.map((filter) => {
					const tag = availableTags?.find((t) => t.TagName === filter.TagName);
					const hasData = tag?.HasData !== false;
					return (
						<Chip
							key={filter.TagName}
							label={`${filter.TagName} = ${filter.Values.join(", ")}`}
							onDelete={() => handleRemoveFilter(filter.TagName)}
							onClick={(e) => handleEditFilter(e, filter)}
							size="small"
							color={hasData ? "primary" : "default"}
							variant="outlined"
							sx={{
								cursor: "pointer",
								opacity: hasData ? 1 : 0.5,
							}}
						/>
					);
				})}

				{isLoadingTags ? (
					<CircularProgress size={20} />
				) : (
					availableTagsForSelection.length > 0 && (
						<Button
							size="small"
							variant="text"
							onClick={handleOpenPopover}
							sx={{ minWidth: "auto", textTransform: "none" }}
						>
							+ Add Tag Filter
						</Button>
					)
				)}

				<Popover
					open={open}
					anchorEl={anchorEl}
					onClose={handleClosePopover}
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "left",
					}}
					transformOrigin={{
						vertical: "top",
						horizontal: "left",
					}}
				>
					<Box
						sx={{
							p: 2,
							minWidth: 350,
							display: "flex",
							flexDirection: "column",
							gap: 2,
						}}
					>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<Typography variant="subtitle2">
								{selectedTag ? "Edit Tag Filter" : "Add Tag Filter"}
							</Typography>
							<FormControlLabel
								control={
									<Checkbox
										size="small"
										checked={showOnlyWithData}
										onChange={(e) => setShowOnlyWithData(e.target.checked)}
									/>
								}
								label={
									<Typography variant="caption">Only with data</Typography>
								}
								sx={{ mr: 0 }}
							/>
						</Box>

						{/* Tag name selection */}
						<Autocomplete
							size="small"
							options={availableTagsForSelection}
							getOptionLabel={(option) => option.TagName}
							value={selectedTag}
							onChange={(_, newValue) => {
								setSelectedTag(newValue);
								setSelectedValues([]);
							}}
							renderOption={(props, option) => (
								<li
									{...props}
									style={{
										opacity: option.HasData ? 1 : 0.8,
									}}
								>
									{option.TagName}
								</li>
							)}
							renderInput={(params) => (
								<TextField {...params} label="Tag Name" variant="outlined" />
							)}
						/>

						{/* Tag values selection */}
						{selectedTag && (
							<>
								{isLoadingValues ? (
									<Box
										sx={{ display: "flex", justifyContent: "center", py: 2 }}
									>
										<CircularProgress size={24} />
									</Box>
								) : (
									<Autocomplete
										multiple
										size="small"
										options={availableTagValues}
										getOptionLabel={(option) => option.Value}
										value={selectedValues}
										onChange={(_, newValue) => setSelectedValues(newValue)}
										isOptionEqualToValue={(option, value) =>
											option.Value === value.Value
										}
										disableCloseOnSelect
										renderOption={(props, option, { selected }) => (
											<li
												{...props}
												style={{
													opacity: option.HasData ? 1 : 0.8,
												}}
											>
												<Checkbox
													icon={icon}
													checkedIcon={checkedIcon}
													style={{ marginRight: 8 }}
													checked={selected}
													size="small"
												/>
												{option.Value}
											</li>
										)}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Tag Values"
												variant="outlined"
												placeholder="Select values..."
											/>
										)}
									/>
								)}
							</>
						)}

						<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
							<Button size="small" onClick={handleClosePopover}>
								Cancel
							</Button>
							<Button
								size="small"
								variant="contained"
								onClick={handleAddFilter}
								disabled={!selectedTag || selectedValues.length === 0}
							>
								{tagFilters.some((f) => f.TagName === selectedTag?.TagName)
									? "Update"
									: "Add"}
							</Button>
						</Box>
					</Box>
				</Popover>
			</Box>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
